import {
  ArchiveRole,
  CareerPeriodType,
  CareerType,
  MemberType,
} from '../../shared/enum/EnumRepository';
import type { SettingData } from '../../shared/dto/EntityRepository';
import { normalizeCareerSectionOrder } from '../../shared/util/careerSectionOrder';

// 기본 스텝들
export const careerSections = [
  { type: CareerType.EDU, storageRange: CareerPeriodType.PERIOD, displayRange: CareerPeriodType.PERIOD },
  { type: CareerType.WRT, storageRange: CareerPeriodType.POINT, displayRange: CareerPeriodType.POINT },
  { type: CareerType.CNS, storageRange: CareerPeriodType.POINT, displayRange: CareerPeriodType.POINT },
  { type: CareerType.S_EXH, storageRange: CareerPeriodType.PERIOD, displayRange: CareerPeriodType.POINT },
  { type: CareerType.G_EXH, storageRange: CareerPeriodType.PERIOD, displayRange: CareerPeriodType.POINT },
  { type: CareerType.BIEN_TRIEN, storageRange: CareerPeriodType.PERIOD, displayRange: CareerPeriodType.POINT },
  { type: CareerType.PRJ, storageRange: CareerPeriodType.POINT, displayRange: CareerPeriodType.POINT },
  { type: CareerType.FAR, storageRange: CareerPeriodType.PERIOD, displayRange: CareerPeriodType.POINT },
  { type: CareerType.AWD_GRT, storageRange: CareerPeriodType.POINT, displayRange: CareerPeriodType.POINT },
  { type: CareerType.PRS, storageRange: CareerPeriodType.POINT, displayRange: CareerPeriodType.POINT },
  { type: CareerType.RSD, storageRange: CareerPeriodType.PERIOD, displayRange: CareerPeriodType.PERIOD },
  { type: CareerType.RPS, storageRange: CareerPeriodType.PERIOD, displayRange: CareerPeriodType.PERIOD },
  { type: CareerType.EXP, storageRange: CareerPeriodType.PERIOD, displayRange: CareerPeriodType.PERIOD },
  { type: CareerType.TCH, storageRange: CareerPeriodType.PERIOD, displayRange: CareerPeriodType.PERIOD },
  { type: CareerType.RSH, storageRange: CareerPeriodType.POINT, displayRange: CareerPeriodType.POINT },
  { type: CareerType.PBL, storageRange: CareerPeriodType.POINT, displayRange: CareerPeriodType.POINT },
  { type: CareerType.COL, storageRange: CareerPeriodType.POINT, displayRange: CareerPeriodType.POINT },
];

const SHARED_CAREER_TYPES = [
  CareerType.EDU,
  CareerType.PRJ,
  CareerType.AWD_GRT,
  CareerType.PRS,
  CareerType.RSD,
  CareerType.PBL,
] as const;

const ARTIST_CAREER_TYPES = new Set<CareerType>([
  ...SHARED_CAREER_TYPES,
  CareerType.S_EXH,
  CareerType.G_EXH,
  CareerType.BIEN_TRIEN,
  CareerType.FAR,
  CareerType.RPS,
  CareerType.COL,
]);

const NON_ARTIST_CAREER_TYPES = new Set<CareerType>([
  ...SHARED_CAREER_TYPES,
  CareerType.EXP,
  CareerType.WRT,
  CareerType.CNS,
  CareerType.TCH,
  CareerType.RSH,
]);

const GROUP_CAREER_TYPES = new Set<CareerType>([
  CareerType.EXP,
  CareerType.G_EXH,
  CareerType.BIEN_TRIEN,
  CareerType.PRJ,
  CareerType.FAR,
  CareerType.AWD_GRT,
  CareerType.PRS,
  CareerType.RSD,
  CareerType.RSH,
  CareerType.PBL,
]);

export const getDefaultCareerSections = (
  setting: Pick<SettingData, 'memberType' | 'archiveRole'> | null | undefined,
  orderedSections = careerSections,
) => {
  let visibleTypes: Set<CareerType>;

  if (setting?.memberType === MemberType.GROUP) {
    visibleTypes = GROUP_CAREER_TYPES;
  } else if (
    setting?.memberType === MemberType.INDIVIDUAL &&
    setting.archiveRole === ArchiveRole.ARTIST
  ) {
    visibleTypes = ARTIST_CAREER_TYPES;
  } else if (
    setting?.memberType === MemberType.INDIVIDUAL &&
    setting.archiveRole != null
  ) {
    visibleTypes = NON_ARTIST_CAREER_TYPES;
  } else {
    return orderedSections;
  }

  return orderedSections.filter((section) => visibleTypes.has(section.type));
};

/** 서버 순서를 metadata에 적용하고, 누락된 타입은 기본 순서로 보충한다. */
export const resolveCareerSections = (
  careerSectionOrder: readonly CareerType[] | null | undefined,
) => {
  const sectionByType = new Map(
    careerSections.map((section) => [section.type, section] as const),
  );

  return normalizeCareerSectionOrder(careerSectionOrder)
    .map((type) => sectionByType.get(type))
    .filter((section): section is (typeof careerSections)[number] => section != null);
};
